import {
  subscribeBazaarSellerInbox,
  subscribeBazaarBuyerInbox,
  type BazaarDMSellerThread,
  type BazaarDMBuyerThread,
} from './bazaarDMs';

type BazaarInboxSnapshot = {
  uid: string;
  sellerThreads: BazaarDMSellerThread[];
  buyerThreads: BazaarDMBuyerThread[];
  sellerReady: boolean;
  buyerReady: boolean;
};

const listeners = new Set<() => void>();
let sellerUnsub: (() => void) | null = null;
let buyerUnsub: (() => void) | null = null;
let snapshot: BazaarInboxSnapshot = {
  uid: '',
  sellerThreads: [],
  buyerThreads: [],
  sellerReady: true,
  buyerReady: true,
};

const cacheByUid = new Map<string, BazaarInboxSnapshot>();

function emit() {
  cacheByUid.set(snapshot.uid, snapshot);
  listeners.forEach((listener) => listener());
}

function stopCurrentSubscriptions() {
  try { sellerUnsub?.(); } catch {}
  try { buyerUnsub?.(); } catch {}
  sellerUnsub = null;
  buyerUnsub = null;
}

export function getBazaarInboxSnapshot(uid?: string): BazaarInboxSnapshot {
  const cleanUid = String(uid || '').trim();
  if (cleanUid && snapshot.uid === cleanUid) return snapshot;
  if (cleanUid && cacheByUid.has(cleanUid)) return cacheByUid.get(cleanUid)!;
  if (!cleanUid) {
    return {
      uid: '',
      sellerThreads: [],
      buyerThreads: [],
      sellerReady: true,
      buyerReady: true,
    };
  }
  return {
    uid: cleanUid,
    sellerThreads: [],
    buyerThreads: [],
    sellerReady: false,
    buyerReady: false,
  };
}

export function subscribeBazaarInboxStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function startBazaarInboxStore(uid: string): void {
  const cleanUid = String(uid || '').trim();
  if (snapshot.uid === cleanUid && (sellerUnsub || buyerUnsub || !cleanUid)) return;

  stopCurrentSubscriptions();

  if (!cleanUid) {
    snapshot = {
      uid: '',
      sellerThreads: [],
      buyerThreads: [],
      sellerReady: true,
      buyerReady: true,
    };
    emit();
    return;
  }

  const cached = cacheByUid.get(cleanUid);
  snapshot = cached
    ? { ...cached, uid: cleanUid }
    : {
        uid: cleanUid,
        sellerThreads: [],
        buyerThreads: [],
        sellerReady: false,
        buyerReady: false,
      };
  emit();

  sellerUnsub = subscribeBazaarSellerInbox(
    cleanUid,
    (rows) => {
      if (snapshot.uid !== cleanUid) return;
      snapshot = { ...snapshot, sellerThreads: rows, sellerReady: true };
      emit();
    },
    (error) => {
      if (snapshot.uid !== cleanUid) return;
      console.error('[BazaarInboxStore] seller inbox subscription failed', error);
      snapshot = { ...snapshot, sellerReady: true };
      emit();
    },
  );

  buyerUnsub = subscribeBazaarBuyerInbox(
    cleanUid,
    (rows) => {
      if (snapshot.uid !== cleanUid) return;
      snapshot = { ...snapshot, buyerThreads: rows, buyerReady: true };
      emit();
    },
    (error) => {
      if (snapshot.uid !== cleanUid) return;
      console.error('[BazaarInboxStore] buyer inbox subscription failed', error);
      snapshot = { ...snapshot, buyerReady: true };
      emit();
    },
  );
}
